<template>
    <div id="client" v-if="$store.state.user">
        <portal v-if="showNavbarClientSection" to="mainNavbarClientSection">
            <main-navbar-client-section :client="client"/>
        </portal>
        <router-view v-if="childReady" v-slot="{ Component }">
            <transition name="fade" mode="out-in">
                <component
                    :is="Component"
                    :key="$route.fullPath"
                    :client="client"
                    class="flex-page-wrapper flex-body flex-container view"
                    @refreshClient="getClient"
                />
            </transition>
        </router-view>
    </div>
</template>

<script>
import MainNavbarClientSection from '@/components/MainNavbarClientSection'
import { clientIsStale, markClientFresh } from '@/client-freshness'

export default {
    components: {MainNavbarClientSection},
    data() {
        return {
            dataLoaded: false,
            warningsChecked: false,
            client: null,
        }
    },
    mounted() {
        this.$store.dispatch('GET_USER').then(() => {
            if (this.pendingLinkClientIds.includes(parseInt(this.clientId))) {
                this.$router.push({name: 'link-client', params: {hashedClientId: this.$route.params.hashedClientId}})
                return
            }

            this.getClient()
            this.getWarnings()
        })
        this.$bus.$on('client_data_updated', this.onClientDataUpdated)
    },
    beforeUnmount() {
        this.$bus.$off('client_data_updated', this.onClientDataUpdated)
    },
    watch: {
        // This component stays mounted across its child routes (meta.stayMounted).
        // Hold the next page back until the critical-warnings check is done, as on
        // mount, and refetch the client only if a write may have changed it.
        $route(to, from) {
            // Leaving this client: App.vue is about to unmount us.
            if (to.matched[0] !== from.matched[0] || to.params.hashedClientId !== from.params.hashedClientId) {
                return
            }
            this.warningsChecked = false
            this.getWarnings()
            if (clientIsStale()) {
                this.getClient()
            }
        },
    },
    computed: {
        clientId() {
            return this.$hasher.decode(this.$route.params.hashedClientId)
        },
        pendingLinkClientIds() {
            let result = []
            if (! this.$store.state.user) {
                return result
            }
            this.$store.state.user.employer_pending_link_clients.forEach((c) => {
                result.push(c.id)
            })

            return result
        },
        showNavbarClientSection() {
            return this.client && (this.client.preview_dashboard || this.$store.state.system_flags?.dashboard_released)
        },
        // wait for the warnings check too, or the page mounts and fetches before a redirect to critical-warnings
        childReady() {
            return this.dataLoaded && this.warningsChecked
        },
    },
    methods: {
        onClientDataUpdated(updates) {
            if (updates.indexOf('security_profiles') >= 0 || updates.indexOf('companies') >= 0 ) {
                this.$api.get(`/clients/${this.clientId}`).then((resp) => {
                    this.client = resp
                }).catch((errors) => {
                    this.$bus.showError(errors.__all__)
                })
            }
        },
        getClient() {
            markClientFresh()
            this.dataLoaded = false
            this.$store.dispatch('START_LOADING')
            this.$api.get(`/clients/${this.clientId}`).then((resp) => {
                this.client = resp
                this.dataLoaded = true
                this.$store.dispatch('STOP_LOADING')
            }).catch((errors) => {
                if (errors.__status__ == 403) {
                    this.$store.dispatch('STOP_LOADING')
                    this.$bus.$emit('no-access')
                    return
                }

                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(errors.__all__)
            })
        },
        getWarnings() {
            if (this.$route.name == 'critical-warnings') {
                this.warningsChecked = true
                return
            }

            const next = this.$route.fullPath
            const url = `/clients/${this.clientId}/warnings/active?critical=1`

            return this.$api.get(url).catch(errors => {
                this.$bus.showError(errors.__all__)
                return []
            }).then(resp => {
                if (resp.filter(w => w.is_critical).length) {
                    return this.$router.replace({name: 'critical-warnings', params: {hashedClientId: this.$route.params.hashedClientId}, query: {next: next}})
                }
            }).finally(() => {
                this.warningsChecked = true
            })
        },
    }
}
</script>
